import lowerFirst from 'lodash/lowerFirst';
import { cn } from 'utils/cn';
import ConditionSwitch from '../../filter-bar/ConditionSwitch';
import { useIsNewDesign } from '../../filter-bar/useIsNewDesign';
import { CompositeFilterForm, CompositePartForm, MainFilterForm, QuickFilterForm } from '../../ListTableTypes';
import { CheckboxGroup } from './CheckboxGroup';
import { DateRange } from './DateRange';
import { Dropdown } from './Dropdown';
import { FilterLabel } from './FilterLabel';
import FilterChecklist from './FilterChecklist';
import { ExcludeToggle } from './filterSelectParts';
import { Interval } from './Interval';
import { Multiselect } from './Multiselect';
import { NumberField } from './NumberField';

type Props = {
    filter: CompositeFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (filter: CompositeFilterForm) => void;
};

/**
 * A composite filter's parts, each drawn by its own control. The drawer heads them with the filter's label and its
 * exclusion, a list part standing in for that label and an interval naming the filter in its first caption, the rest
 * in a row that wraps before a control drops under 9rem, a checkbox group on a row of its own; the new design's pane,
 * which shows the label and the condition switch itself, stacks them.
 */
export function Composite({ filter, quickFilters, mainFilters, onChange }: Props) {
    const isNewDesign = useIsNewDesign();
    // This render's copies, written in place: a date range's quick pick reports both of its ends in one event.
    const parts = filter.parts.map((part) => ({ ...part }));
    const write = (index: number, change: Partial<CompositePartForm>) => {
        Object.assign(parts[index], change);
        onChange({ ...filter, parts });
    };
    const exclude = (isExcluding: boolean) => onChange({ ...filter, parts, isExcluding });

    const control = (part: CompositePartForm, index: number) => {
        switch (part.type) {
            case 'multiselect':
                return isNewDesign ? (
                    // Five and a half rows, so the next one peeks.
                    <div key={part.selector} className="flex max-h-[13.5rem] min-h-0 flex-col">
                        <FilterChecklist
                            filter={part}
                            label={part.label ?? ''}
                            quickFilters={quickFilters}
                            mainFiltersInForm={[]}
                            onChange={(selected) => write(index, { selected })}
                        />
                    </div>
                ) : (
                    <Multiselect
                        key={part.selector}
                        filter={part}
                        quickFilters={quickFilters}
                        mainFilters={mainFilters}
                        onChange={(selected) => write(index, { selected })}
                        onExcludingChange={exclude}
                    />
                );
            case 'daterange':
                return (
                    <DateRange
                        key={part.selector}
                        filter={part}
                        stacked={isNewDesign}
                        quickFilters={quickFilters}
                        mainFilters={mainFilters}
                        onChange={(value, _key, setMin) =>
                            write(index, setMin ? { from: value as string | null } : { to: value as string | null })
                        }
                    />
                );
            case 'interval':
                return (
                    <Interval
                        key={part.selector}
                        filter={part}
                        onChange={(value, _key, setMin) =>
                            write(index, setMin ? { from: value as number } : { to: value as number })
                        }
                    />
                );
            case 'number':
                return (
                    <NumberField
                        key={part.selector}
                        filter={part}
                        quickFilters={quickFilters}
                        mainFilters={mainFilters}
                        onChange={(value) => write(index, { value })}
                    />
                );
            case 'dropdown':
                // A dropdown part always holds one of its options.
                return isNewDesign ? (
                    <div key={part.selector} className="flex flex-col gap-2">
                        <FilterLabel label={part.label ?? ''} />
                        <ConditionSwitch
                            aria-label={`${filter.label ?? ''} ${(part.label ?? '').toLocaleLowerCase()}`}
                            items={part.options.map((option) => ({ value: String(option.value), label: option.label }))}
                            value={part.selected ? String(part.selected.value) : ''}
                            onChange={(next) => {
                                const option = part.options.find((candidate) => String(candidate.value) === next);
                                if (option) write(index, { selected: option });
                            }}
                        />
                    </div>
                ) : (
                    <Dropdown
                        key={part.selector}
                        filter={part}
                        clearable={false}
                        quickFilters={quickFilters}
                        mainFilters={mainFilters}
                        onChange={(selected) => write(index, { selected: selected ?? part.options[0] ?? null })}
                    />
                );
            case 'checkboxgroup':
                return (
                    <CheckboxGroup
                        key={part.selector}
                        filter={part}
                        direction={part.displayDirection ?? 'row'}
                        quickFilters={quickFilters}
                        mainFilters={mainFilters}
                        onChange={(checked, _argument, _key, key) =>
                            write(index, {
                                filters: part.filters.map((option) => ({
                                    ...option,
                                    isSelected:
                                        option.key === key
                                            ? checked
                                            : checked && part.multiple !== true
                                              ? false
                                              : option.isSelected,
                                })),
                            })
                        }
                    />
                );
        }
    };

    if (isNewDesign) return <div className="flex flex-col gap-3">{parts.map(control)}</div>;

    const [head] = parts;
    const headIsList = head?.type === 'multiselect';
    const namedInterval =
        head?.type === 'interval' && !filter.canExclude
            ? {
                  ...head,
                  minLabel: `${filter.label ?? ''} ${lowerFirst(head.minLabel)}`.trim(),
                  maxLabel: lowerFirst(head.maxLabel),
              }
            : null;
    return (
        <div className="flex flex-col gap-2">
            {headIsList ? (
                control(
                    {
                        ...head,
                        label: filter.label,
                        tooltip: filter.tooltip,
                        tooltipValues: filter.tooltipValues,
                        canExclude: filter.canExclude,
                        isExcluding: filter.isExcluding,
                        controlTooltips: filter.controlTooltips,
                    },
                    0
                )
            ) : namedInterval ? null : (
                <FilterLabel label={filter.label ?? ''} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues}>
                    {filter.canExclude ? (
                        <ExcludeToggle
                            checked={!!filter.isExcluding}
                            onChange={exclude}
                            tooltip={filter.controlTooltips?.exclude}
                        />
                    ) : null}
                </FilterLabel>
            )}
            <div className="flex flex-row flex-wrap items-end gap-4">
                {parts.map((part, index) =>
                    headIsList && index === 0 ? null : (
                        <div
                            key={part.selector}
                            className={cn(
                                'flex min-w-[min(9rem,100%)] grow basis-0',
                                (part.type === 'daterange' || part.type === 'interval') && 'min-w-[min(18rem,100%)]',
                                part.type === 'checkboxgroup' && 'basis-full'
                            )}
                        >
                            {control(index === 0 && namedInterval ? namedInterval : part, index)}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
